/**
 * AI Course Creation Page
 *
 * This page allows users to create AI-generated courses by:
 * 1. Preparation: Getting an AI-generated course outline
 * 2. Creation: Creating the course based on the outline
 *
 * The page is split into two main steps:
 * - PreparationStep: Handles outline generation with form inputs
 * - CreationStep: Handles course creation after outline is received
 */

import React, { useState, useEffect } from 'react'
import { withStyles } from '@material-ui/core/styles'
import { useSelector, useDispatch } from 'react-redux'
import { Typography, CircularProgress } from '@material-ui/core'
import styles from './styles'
import {
  PreparationStep,
  CreationStep,
} from 'src/pages/ai-course-creation/components'
import { aICreatorClearOutlineAction } from 'src/reducers/ai-creator/action'
import { userLimitsGetAction } from 'src/reducers/user-limits/action'
import {
  selectUserLimits,
  selectCurrentCredits,
} from 'src/reducers/user-limits/selector'
import { LimitTypeEnum } from 'src/model/limit'
import AICreationLimitModal from 'src/components/ai-creation-limit-modal/index'
import { useHistory } from 'react-router-dom'

const AiCourseCreation = props => {
  const { classes } = props
  const dispatch = useDispatch()
  const history = useHistory()
  // User limits and credits state
  const userLimits = useSelector(selectUserLimits)
  const currentCredits = useSelector(selectCurrentCredits)
  const [limitLoading, setLimitLoading] = useState(true)
  const [roleType, setRoleType] = useState()
  const [refreshesAt, setRefreshesAt] = useState()

  // State to manage which step we're on
  const [currentStep, setCurrentStep] = useState('preparation') // 'preparation' or 'creation'
  const [generatedOutline, setGeneratedOutline] = useState(null)
  const [documentFollowing, setDocumentFollowing] = useState('follow')

  const [courseLanguageCode, setCourseLanguageCode] = useState('en')

  // Image generation state
  const [imageWillBeGenerated, setImageWillBeGenerated] = useState(false)

  // Track if user started with sufficient credits and if they've begun course creation
  const [
    startedWithSufficientCredits,
    setStartedWithSufficientCredits,
  ] = useState(false)

  // Get outline state from Redux store
  const outline = useSelector(state => state.aICreator.outline)

  useEffect(() => {
    dispatch(aICreatorClearOutlineAction())
    setCurrentStep('preparation')

    // Get user limits on component mount
    if (!userLimits) {
      dispatch(userLimitsGetAction())
    }
  }, [dispatch, userLimits])

  useEffect(() => {
    if (userLimits) {
      setLimitLoading(true)
      const aiLimitData = userLimits.find(
        x => x.limit.type === LimitTypeEnum.AICourseCreation,
      )

      if (aiLimitData) {
        const refreshedAt = aiLimitData.refreshedAt
        setRoleType(userLimits[0].limit.roleType)
        setRefreshesAt(refreshedAt)
        // Set flag if user started with sufficient credits (more than 10)
        // Only update to true, never revert back to false once set
        if (currentCredits > 10) {
          setStartedWithSufficientCredits(true)
        }
      }
      setLimitLoading(false)
    }
  }, [userLimits, currentCredits])

  // Handle outline generation completion
  const handleOutlineGenerated = outline => {
    setGeneratedOutline(outline)
    setCurrentStep('creation')
    // Mark that user has started creating a course
    setStartedWithSufficientCredits(true)
  }

  // Handle going back to preparation step
  const handleBackToPreparation = () => {
    setCurrentStep('preparation')
    setGeneratedOutline(null)
  }

  const handleClickRedirect = () => {
    history.push('/pricing')
  }

  // Show loading state while fetching limits
  if (limitLoading) {
    return (
      <div
        className={classes.root}
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '200px',
        }}
      >
        <CircularProgress />
      </div>
    )
  }

  // Show limit warning for users with low credits ONLY if they haven't started creating a course
  if (!startedWithSufficientCredits && currentCredits <= 10) {
    return (
      <div className={classes.limitModalWrapper}>
        <AICreationLimitModal
          handleClickRedirect={() => {
            handleClickRedirect()
          }}
          refreshesAt={refreshesAt}
          roleType={roleType}
          type={'ai-course-creation'}
        />
      </div>
    )
  }

  // Render the appropriate step component for eligible users
  if (currentStep === 'preparation') {
    return (
      <div className={classes.root}>
        <PreparationStep
          onOutlineGenerated={handleOutlineGenerated}
          imageWillBeGenerated={imageWillBeGenerated}
          setImageWillBeGenerated={setImageWillBeGenerated}
          documentFollowing={documentFollowing}
          setDocumentFollowing={setDocumentFollowing}
          courseLanguageCode={courseLanguageCode}
          setCourseLanguageCode={setCourseLanguageCode}
        />
      </div>
    )
  }

  if (currentStep === 'creation') {
    return (
      <div className={classes.root}>
        <CreationStep
          outline={generatedOutline || outline}
          onBackToOutline={handleBackToPreparation}
          imageWillBeGenerated={imageWillBeGenerated}
          setImageWillBeGenerated={setImageWillBeGenerated}
          documentFollowing={documentFollowing}
          setDocumentFollowing={setDocumentFollowing}
        />
      </div>
    )
  }

  return null
}

export default withStyles(styles)(AiCourseCreation)
